import { useEffect, useRef } from 'react';

/**
 * Hook to display a badge on the favicon and PWA app icon
 * - Favicon badge: Works in browser tabs
 * - App Badge API: Works for installed PWAs on Android/Desktop
 */
export const useFaviconBadge = (unreadCount) => {
    const originalFaviconRef = useRef(null);
    const canvasRef = useRef(null);

    // Handle PWA App Badge (for installed apps on homescreen)
    useEffect(() => {
        const updateAppBadge = async () => {
            // Check if App Badge API is supported
            if ('setAppBadge' in navigator) {
                try {
                    if (unreadCount && unreadCount > 0) {
                        // Set badge with count (or just dot if count > 99)
                        await navigator.setAppBadge(unreadCount);
                    } else {
                        // Clear the badge
                        await navigator.clearAppBadge();
                    }
                } catch (error) {
                    // Silently fail - API might not be available in current context
                    console.debug('App Badge API not available:', error.message);
                }
            }
        };

        updateAppBadge();
    }, [unreadCount]);

    // Handle Favicon badge (for browser tabs)
    useEffect(() => {
        // Get the current favicon
        const favicon = document.querySelector('link[rel="icon"][sizes="32x32"]') || 
                       document.querySelector('link[rel="shortcut icon"]') ||
                       document.querySelector('link[rel="icon"]');
        
        if (!favicon) return;

        // Store original favicon URL
        if (!originalFaviconRef.current) {
            originalFaviconRef.current = favicon.href;
        }

        // If no unread items, restore original favicon
        if (!unreadCount || unreadCount === 0) {
            favicon.href = originalFaviconRef.current;
            return;
        }

        // Create canvas to draw badge
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
            canvasRef.current.width = 32;
            canvasRef.current.height = 32;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Load the original favicon image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            // Clear canvas
            ctx.clearRect(0, 0, 32, 32);
            
            // Draw original favicon
            ctx.drawImage(img, 0, 0, 32, 32);
            
            // Draw red dot - larger, in bottom-right corner (no white border)
            const dotSize = 18;
            const dotX = 32 - dotSize / 2 - 1; // Right side with small margin
            const dotY = 32 - dotSize / 2 - 1; // Bottom with small margin
            
            // Red dot only
            ctx.beginPath();
            ctx.arc(dotX, dotY, dotSize / 2, 0, 2 * Math.PI);
            ctx.fillStyle = '#ef4444';
            ctx.fill();
            
            // Update favicon
            favicon.href = canvas.toDataURL('image/png');
        };
        
        img.onerror = () => {
            // If image fails to load, just draw a simple badge
            ctx.clearRect(0, 0, 32, 32);
            
            // Draw a simple colored square as fallback
            ctx.fillStyle = '#6366f1';
            ctx.fillRect(0, 0, 32, 32);
            
            // Draw red dot - larger, in bottom-right corner (no white border)
            const dotSize = 18;
            const dotX = 32 - dotSize / 2 - 1;
            const dotY = 32 - dotSize / 2 - 1;
            
            ctx.beginPath();
            ctx.arc(dotX, dotY, dotSize / 2, 0, 2 * Math.PI);
            ctx.fillStyle = '#ef4444';
            ctx.fill();
            
            favicon.href = canvas.toDataURL('image/png');
        };

        img.src = originalFaviconRef.current;

        // Cleanup - restore original favicon when component unmounts
        return () => {
            if (originalFaviconRef.current && favicon) {
                favicon.href = originalFaviconRef.current;
            }
        };
    }, [unreadCount]);
};

export default useFaviconBadge;
